import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { realtimeSubscribeWorkspace } from '../../redux/actions/realtime';
import { 
  Box, Typography, Paper, Grid, CircularProgress, Divider, 
  Chip, Button, Alert, Dialog, DialogTitle, DialogContent, 
  DialogContentText, DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DnsIcon from '@mui/icons-material/Dns';
import InfoIcon from '@mui/icons-material/Info';
import LabelIcon from '@mui/icons-material/Label';
import CategoryIcon from '@mui/icons-material/Category';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import { useTheme } from '@mui/material/styles';
import { format } from '../../utils/datetime';
import { fetchRequest as fetchWorkspaceDetail, clearState as clearDetailState } from '../../redux/actions/workspaces/detail';
import {
  fetchRequest as deleteWorkspace,
  clearState as clearDeleteState,
  clearMarketplaceConflict as clearWorkspaceDeleteMarketplaceConflict,
} from '../../redux/actions/workspaces/delete';
import { fetchRequest as getUser } from '../../redux/actions/user/getUser';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import WorkspaceMembersPanel from './WorkspaceMembersPanel';
import { useSnackbar } from 'notistack';

const WorkspaceDetailPage = ({ 
  workspaceDetail,
  workspaceDetailLoading,
  workspaceDetailError,
  fetchWorkspaceDetailAction,
  deleteWorkspaceAction,
  deleteLoading,
  deleteResponse,
  deleteError,
  deleteMarketplaceConflict,
  clearDeleteStateAction,
  clearDeleteMarketplaceConflictAction,
  getUser
}) => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Fetch workspace details on component mount
  useEffect(() => {
    if (workspaceId) {
      fetchWorkspaceDetailAction(workspaceId);
    }
  }, [workspaceId, fetchWorkspaceDetailAction]);

  // Handle delete success/error
  useEffect(() => {
    if (deleteResponse) {
      enqueueSnackbar('Workspace deleted successfully', { variant: 'success' });
      clearDeleteStateAction();
      getUser();
      navigate('/workspaces');
    }
    // We intentionally skip the generic "Error deleting workspace" toast
    // when the failure carries the marketplace-conflict flag - that's
    // an interactive prompt handled by the cascade dialog below.
    if (deleteError && !deleteError?.conflict && !deleteMarketplaceConflict) {
      enqueueSnackbar('Error deleting workspace', { variant: 'error' });
      setIsDeleteDialogOpen(false);
    }
  }, [
    deleteResponse, deleteError, deleteMarketplaceConflict,
    clearDeleteStateAction, navigate, enqueueSnackbar, getUser,
  ]);

  // The first delete attempt opens the confirm dialog. If the BE
  // rejects with 409 + marketplace-conflict, we close that initial
  // dialog so the cascade dialog (a modal that explains the impact)
  // takes the spotlight.
  useEffect(() => {
    if (deleteMarketplaceConflict) {
      setIsDeleteDialogOpen(false);
    }
  }, [deleteMarketplaceConflict]);

  const handleBack = () => {
    navigate('/workspaces');
  };

  const handleEdit = () => {
    navigate(`/workspace/${workspaceId}/edit`);
  };

  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    deleteWorkspaceAction(workspaceId, false);
  };

  // ----- marketplace cascade-delete confirm -----------------------------
  const dismissCascade = () => {
    if (clearDeleteMarketplaceConflictAction) {
      clearDeleteMarketplaceConflictAction();
    }
  };
  const confirmCascadeDelete = () => {
    deleteWorkspaceAction(workspaceId, true);
    dismissCascade();
  };

  const handleSwitchToWorkspace = () => {
    // Set this workspace as the active workspace. The localStorage key is
    // WORKSPACE_ID for backwards compatibility with existing deployments —
    // it's an internal identifier, not user-visible.
    localStorage.setItem('WORKSPACE_ID', workspaceId);

    // Point the realtime stream at the newly-active workspace (releases the
    // previous workspace channel, subscribes the new one).
    dispatch(realtimeSubscribeWorkspace(workspaceId));

    enqueueSnackbar(`Switched to workspace: ${workspace.name}`, { variant: 'success' });
    
    navigate('/');
  };

  // Show loading state
  if (workspaceDetailLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading workspace details...
        </Typography>
      </Box>
    );
  }

  // Extract workspace data from API response
  const workspace = workspaceDetailError || !workspaceDetail ? null : workspaceDetail.data || workspaceDetail;

  // Show workspace not found
  if (workspaceDetailError || !workspace) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        pt: 4,
        px: 3
      }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
          <Typography variant="h6">Workspace not found</Typography>
          <Typography variant="body2">
            The workspace you're trying to view could not be found or you don't have permission to access it.
          </Typography>
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant="outlined"
        >
          Back to workspaces
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* Breadcrumb Navigation */}
        <PageBreadcrumbs 
          items={[
            {
              text: "Workspaces",
              icon: <DnsIcon />,
              link: "/workspaces"
            },
            {
              text: workspace.name || workspace.uid
            }
          ]}
          sx={{ mb: 2 }}
        />

        {/* Action Buttons Row */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex' }}>
            {/* Switch to Workspace Button */}
            <Button
              variant="contained"
              startIcon={<SwitchAccountIcon />}
              sx={{ mr: 2, px: 2 }}
              size="small"
              onClick={handleSwitchToWorkspace}
            >
              Switch to workspace
            </Button>
            
            {/* Edit Button */}
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              sx={{ mr: 2, px: 2 }}
              size="small"
              onClick={handleEdit}
            >
              Edit
            </Button>
            
            {/* Delete Button */}
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ px: 2 }}
              size="small"
              onClick={handleOpenDeleteDialog}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Workspace Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Workspace details
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Name
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {workspace.name}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Created
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {workspace.created_at ? format(new Date(workspace.created_at), 'PPP') : 'Unknown'}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body1">
              {workspace.description}
            </Typography>
          </Grid>

          {workspace.organization_name && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Organization
              </Typography>
              <Chip 
                label={workspace.organization_name}
                size="small"
                color="info"
                variant="outlined"
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Workspace members — grants limited to organization members.
          (Moved here from Settings; org membership is managed in
          Settings -> Organization Members.) */}
      <WorkspaceMembersPanel
        workspaceId={workspaceId}
        organizationId={workspace.organization_id}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete workspace</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{workspace?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Marketplace cascade-delete confirmation. Opens automatically
          when the BE returns 409 because at least one signal in this
          workspace is published with active subscribers. "Delist all &
          Delete workspace" re-calls /workspace/{id} with
          force=true; the BE then cascade-cancels every subscription
          across every listing in the workspace before tearing down the
          workspace's data. */}
      {(() => {
        const isAdvisory = deleteMarketplaceConflict?.kind === 'published_marketplace_listing';
        const listingsArr = deleteMarketplaceConflict?.listings || [];
        const subs = deleteMarketplaceConflict?.active_subscribers || 0;
        return (
          <Dialog
            open={!!deleteMarketplaceConflict}
            onClose={dismissCascade}
            maxWidth="sm"
            fullWidth
            aria-labelledby="workspace-delete-cascade-title"
          >
            <DialogTitle
              id="workspace-delete-cascade-title"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box
                component="span"
                sx={{ color: isAdvisory ? 'info.main' : 'warning.main' }}
              >
                {isAdvisory ? 'ⓘ' : '⚠'}
              </Box>
              {isAdvisory
                ? 'This workspace has marketplace listings'
                : 'This workspace has active marketplace subscribers'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Box sx={{ mb: 1.5 }}>
                  {isAdvisory ? (
                    <>
                      "{workspace?.name}" still has{' '}
                      <strong>{listingsArr.length}</strong> marketplace
                      listing{listingsArr.length === 1 ? '' : 's'}. Nobody
                      is subscribed yet.
                    </>
                  ) : (
                    <>
                      "{workspace?.name}" has{' '}
                      <strong>{listingsArr.length}</strong> published listing
                      {listingsArr.length === 1 ? '' : 's'}
                      {' '}with <strong>{subs}</strong> active subscriber
                      {subs === 1 ? '' : 's'} total.
                    </>
                  )}
                </Box>
                {listingsArr.length > 0 && (
                  <Box
                    sx={{
                      mt: 1,
                      mb: 1.5,
                      p: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      maxHeight: 180,
                      overflowY: 'auto',
                    }}
                  >
                    {listingsArr.map((l) => (
                      <Box
                        key={l.listing_id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          py: 0.25,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {l.title || l.listing_id}
                          {l.signal_key && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({l.signal_key})
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {l.subscribers || 0} subscriber
                          {l.subscribers === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {isAdvisory ? (
                  <>
                    Continuing will{' '}
                    <strong>delist every listing</strong> and then delete
                    the workspace. No buyers will be affected. This{' '}
                    <strong>cannot be undone</strong>.
                  </>
                ) : (
                  <>
                    Continuing will <strong>delist every listing</strong>
                    {' '}in this workspace and{' '}
                    <strong>cancel every subscription</strong>
                    {' '}immediately. Buyers stop being charged starting
                    today and their segments will lose this data on the
                    next refresh. The workspace itself will then be deleted.
                    This <strong>cannot be undone</strong>.
                  </>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={dismissCascade} disabled={deleteLoading}>
                Keep listings live
              </Button>
              <Button
                onClick={confirmCascadeDelete}
                color="error"
                variant="contained"
                disabled={deleteLoading}
                startIcon={deleteLoading
                  ? <CircularProgress size={16} />
                  : <DeleteIcon />}
              >
                {deleteLoading
                  ? 'Deleting…'
                  : (isAdvisory
                      ? 'Delist all & Delete workspace'
                      : 'Delist all & Delete workspace')}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  workspaceDetail: state.workspace_detail.response,
  workspaceDetailLoading: state.workspace_detail.isLoading,
  workspaceDetailError: state.workspace_detail.error,
  deleteLoading: state.workspace_delete.isLoading,
  deleteResponse: state.workspace_delete.response,
  deleteError: state.workspace_delete.error,
  deleteMarketplaceConflict: state.workspace_delete.marketplaceConflict,
});

const mapDispatchToProps = (dispatch) => ({
  fetchWorkspaceDetailAction: (workspaceId) => dispatch(fetchWorkspaceDetail(workspaceId)),
  deleteWorkspaceAction: (workspaceId, force = false) =>
    dispatch(deleteWorkspace(workspaceId, force)),
  clearDeleteStateAction: () => dispatch(clearDeleteState()),
  clearDeleteMarketplaceConflictAction: () =>
    dispatch(clearWorkspaceDeleteMarketplaceConflict()),
  getUser: () => dispatch(getUser()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkspaceDetailPage);
